import {
  AdminSpinner,
  ArrowRed,
  BestPerformingStateEntity,
  Box,
  Div,
  getDashboardBestPerformingStates,
  H5,
  Icon,
  lightTheme,
  ModalPortal,
  BestPerformingPlaceholder,
  BestPerformingStateComponent,
  reportError,
} from '@vgrubs/components';
import { Dispatch, SetStateAction, useEffect, useState } from 'react';
import { useInView } from 'react-intersection-observer';

async function getBestPerformingStates(
  page: number,
  bestPerformingStates: BestPerformingStateEntity[] | null,
  setBestPerformingStates: Dispatch<
    SetStateAction<BestPerformingStateEntity[] | null>
  >,
  hasMore: boolean,
  setHasMore: Dispatch<SetStateAction<boolean>>,
  setPage: Dispatch<SetStateAction<number>>,
  setLoading: Dispatch<SetStateAction<boolean>>
) {
  if (!hasMore) return;
  setLoading(true);

  const LIMIT = 10;

  try {
    const { data } = await getDashboardBestPerformingStates({
      page,
      limit: LIMIT,
    });

    if (bestPerformingStates === null) {
      setBestPerformingStates(data.data);
    } else {
      setBestPerformingStates((prev) => [...(prev || []), ...data.data]);
    }

    setHasMore(data.data.length === LIMIT);
    setPage(page + 1);
  } catch (error) {
    reportError(error);
  } finally {
    setLoading(false);
  }
}

export const BestPerformingStatesSection = () => {
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const [bestPerformingStates, setBestPerformingStates] = useState<
    BestPerformingStateEntity[] | null
  >(null);
  const [hasMore, setHasMore] = useState(true);
  const [page, setPage] = useState(1);
  const [ref, inView] = useInView();

  useEffect(() => {
    setPage(1);
    setHasMore(true);
    getBestPerformingStates(
      1,
      null,
      setBestPerformingStates,
      true,
      setHasMore,
      setPage,
      setLoading
    );
  }, []);

  useEffect(() => {
    if (inView && hasMore) {
      getBestPerformingStates(
        page,
        bestPerformingStates,
        setBestPerformingStates,
        hasMore,
        setHasMore,
        setPage,
        setLoadingMore
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inView]);

  return (
    <>
      <Box $p="16px 20px" $relative $overflow="hidden">
        {loading && <AdminSpinner small absolute creme />}
        {bestPerformingStates?.length ? (
          <Icon
            $pointer
            style={{ position: 'absolute', right: 16, top: 16 }}
            onClick={() => setIsOpen(true)}
            color={lightTheme.lightGray}
          >
            <ArrowRed />
          </Icon>
        ) : null}
        {<H5 $mb="16">Best Performing States</H5>}

        {bestPerformingStates?.slice(0, 5).map((item, index) => (
          <BestPerformingStateComponent key={index} item={item} index={index} />
        ))}

        <BestPerformingPlaceholder length={bestPerformingStates?.length || 0} />
      </Box>
      <ModalPortal
        isOpen={isOpen}
        closeModal={() => setIsOpen(false)}
        backgroundColor={lightTheme.white}
        padding="24px"
        logo={false}
        width="600px"
        maxHeight="calc(65px * 10)"
        modalOverflow="hidden"
        titleWithExit="Best Performing States"
      >
        {loadingMore && <AdminSpinner small absolute />}

        {bestPerformingStates?.map((item, index) => (
          <BestPerformingStateComponent key={index} item={item} index={index} />
        ))}
        <Div $height="20px" ref={ref}></Div>
      </ModalPortal>
    </>
  );
};
